import { useEffect, useState } from 'react';
import { X, Download, Sparkles, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import collectDeviceInfo from '@/lib/deviceInfo';

// The current mobile app's build info is embedded at build time via env vars
// so we can compare with what the server says is the latest available.
// GitHub Actions sets these (see workflow lines VERSION_CODE / VERSION_NAME).
const CURRENT_CODE = Number(process.env.REACT_APP_VERSION_CODE || 0);
const CURRENT_NAME = String(process.env.REACT_APP_VERSION_NAME || 'web');
const SNOOZE_KEY = 'app_update_snoozed_until';
const HEARTBEAT_KEY = 'app_last_heartbeat_at';

/**
 * AppUpdateBanner
 * — Polls /api/app/version-info on mount.
 * — If server's latest_version_code > our built-in code AND we have a download URL,
 *   shows a friendly gradient banner with an "Update Now" button.
 * — Users can dismiss (24 h snooze in localStorage) unless force_update = true.
 * — Renders nothing in web-only mode (versionCode not set at build time).
 *
 * Side effect: fires a `POST /api/app/heartbeat` at most once per day so admin
 * can see installed-version distribution. This runs even when there is no
 * update available, and only in the actual APK/Capacitor build.
 */
export default function AppUpdateBanner() {
    const [info, setInfo] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    // Send heartbeat once per day (native builds only)
    useEffect(() => {
        const isNative = window?.Capacitor?.isNativePlatform?.() || CURRENT_CODE > 0;
        if (!isNative) return;
        const lastAt = Number(localStorage.getItem(HEARTBEAT_KEY) || 0);
        // 20 h dedupe so multiple app launches in a day don't spam the API
        if (lastAt && (Date.now() - lastAt) < 20 * 60 * 60 * 1000) return;
        (async () => {
            try {
                const info = await collectDeviceInfo();
                await api.appHeartbeat({
                    device_id: info.device_id || 'unknown',
                    version_code: CURRENT_CODE || 0,
                    version_name: CURRENT_NAME,
                    platform: info.platform || 'android',
                    model: info.device_model || '',
                    os_version: info.os_version || '',
                });
                localStorage.setItem(HEARTBEAT_KEY, String(Date.now()));
            } catch { /* silent — heartbeat is best-effort */ }
        })();
    }, []);

    useEffect(() => {
        // Skip entirely in dev/web when we can't compare versions
        if (!CURRENT_CODE) return;
        // Check snooze
        const snoozeUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
        if (snoozeUntil && Date.now() < snoozeUntil) setDismissed(true);
        api.appVersionInfo().then(setInfo).catch(() => setInfo(null));
    }, []);

    if (!CURRENT_CODE || !info) return null;
    if (!info.apk_download_url) return null;
    if (info.latest_version_code <= CURRENT_CODE) return null;

    const force = !!info.force_update
        || (info.minimum_supported_version_code && CURRENT_CODE < info.minimum_supported_version_code);
    if (dismissed && !force) return null;

    const snoozeAndClose = () => {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
        setDismissed(true);
    };

    // ── Blocking full-screen dialog on FORCE UPDATE ──
    // When force=true, install is below the min-supported version, so the app
    // must not be usable until the user downloads the new APK. This overlay
    // covers everything with no dismiss control.
    if (force) {
        return (
            <div
                className='fixed inset-0 z-[999] flex items-center justify-center p-4'
                style={{ background: 'rgba(15,23,42,.92)', backdropFilter: 'blur(6px)' }}
                data-testid='app-force-update-dialog'
                role='alertdialog'
                aria-modal='true'
            >
                <div className='w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden'>
                    <div className='p-5 flex items-center gap-3' style={{ background: 'linear-gradient(120deg,#1E2E7A 0%,#F26522 130%)' }}>
                        <div className='w-12 h-12 rounded-2xl bg-white/15 grid place-items-center flex-shrink-0'>
                            <ShieldAlert className='w-6 h-6 text-white' />
                        </div>
                        <div className='min-w-0'>
                            <div className='text-white font-semibold text-[16px]'>Update Required</div>
                            <div className='text-white/80 text-[11.5px]'>ऐप्लिकेशन को उपयोग करने के लिए नया version install करें</div>
                        </div>
                    </div>
                    <div className='p-5 space-y-3'>
                        <div className='text-[13px] text-slate-700'>
                            आपके पास <b>v{CURRENT_NAME || CURRENT_CODE}</b> installed है। यह version अब support नहीं करता है।
                            कृपया नया version <b className='text-slate-900'>v{info.latest_version_name}</b> install करें।
                        </div>
                        {info.release_notes && (
                            <div className='rounded-md border p-2.5 bg-slate-50 text-[11.5px] text-slate-600'>
                                <div className='font-semibold text-slate-800 mb-1 text-[11px] uppercase tracking-wider'>What's new</div>
                                <div className='whitespace-pre-line'>{info.release_notes}</div>
                            </div>
                        )}
                        <a
                            href={info.apk_download_url}
                            target='_blank'
                            rel='noreferrer'
                            data-testid='app-force-update-download'
                            className='block'
                        >
                            <Button
                                size='lg'
                                className='w-full text-white font-semibold'
                                style={{ background: 'linear-gradient(135deg,#F26522,#FF9A5A)' }}
                            >
                                <Download className='w-4 h-4 mr-2' /> Update Now · डाउनलोड करें
                            </Button>
                        </a>
                        <p className='text-[10px] text-slate-500 text-center'>
                            Download पूरा होने पर APK file open करें → "Install" tap करें
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Soft banner (dismissible) — new version available but current still supported ──

    return (
        <div
            className={`sticky top-0 z-40 border-b ${force ? 'bg-red-500/10 border-red-500/40' : 'bg-primary/5 border-primary/30'}`}
            data-testid='app-update-banner'
        >
            <div className='max-w-6xl mx-auto flex items-center gap-3 px-4 py-2.5'>
                <div className={`w-8 h-8 rounded-full flex-shrink-0 grid place-items-center ${force ? 'bg-red-500 text-white' : 'bg-primary text-primary-foreground'}`}>
                    {force ? <ShieldAlert className='w-4 h-4' /> : <Sparkles className='w-4 h-4' />}
                </div>
                <div className='flex-1 min-w-0'>
                    <div className='text-sm font-medium'>
                        {force ? 'A required update is available' : 'New version available'}
                        <span className='ml-1.5 inline-flex items-center rounded-full bg-background border px-1.5 py-0.5 text-[10px] font-mono'>
                            v{info.latest_version_name}
                        </span>
                    </div>
                    <div className='text-[11px] text-muted-foreground truncate'>
                        {info.release_notes || 'Bug fixes and improvements'}
                        {CURRENT_NAME && <span className='ml-1'>· you have v{CURRENT_NAME}</span>}
                    </div>
                </div>
                <a href={info.apk_download_url} target='_blank' rel='noreferrer' data-testid='app-update-download'>
                    <Button size='sm' className={force ? 'bg-red-600 hover:bg-red-700 text-white' : ''}>
                        <Download className='w-3.5 h-3.5 mr-1' /> {force ? 'Update Now' : 'Get Update'}
                    </Button>
                </a>
                {!force && (
                    <button
                        onClick={snoozeAndClose}
                        className='w-8 h-8 rounded-full grid place-items-center hover:bg-muted flex-shrink-0'
                        aria-label='Dismiss update prompt'
                        data-testid='app-update-dismiss'
                    >
                        <X className='w-4 h-4' />
                    </button>
                )}
            </div>
        </div>
    );
}
